import type {
  CloudProgressRead,
  CloudGateway,
  CloudUser,
  CloudWriteFailure,
  CloudWriteResult,
  DebugEntitlementState,
} from "../cloud/gateway";
import {
  GUEST_PROGRESS_KEY,
  uidProgressKey,
  type LocalProgressStore,
  type LocalProgressEnvelope,
} from "./local-store";
import {
  mergeProgress,
  progressContentEqual,
  progressIsMonotonicFrom,
  progressNeedsExplicitSource,
  type ExplicitProgressSource,
} from "./merge";
import {
  progressOwnerScope,
  recoverEndlessRecords,
  type EndlessCheckpoint,
  type EndlessSessionJournal,
  type MarkAssistedResult,
  type MatchPersistenceStore,
  type MatchPersistenceMode,
  type ProgressOwnerScope,
} from "./match-persistence";
import {
  cloneProgress,
  createDefaultProgress,
  validateProgress,
  type Progress,
  type ProgressSettings,
} from "./schema";

export type ProgressSessionMode =
  | "guest"
  | "auth-unauthorized"
  | "account"
  | "syncing"
  | "offline"
  | "conflict"
  | "storage-unavailable";

export interface ProgressConflict {
  local: Progress;
  cloud: Progress;
  needsExplicitSource: boolean;
}

export interface ProgressSnapshot {
  mode: ProgressSessionMode;
  progress: Progress;
  user: CloudUser | null;
  conflict: ProgressConflict | null;
  recoveredCorruptProgress: boolean;
  message: string;
  cloudWriteDiagnostic: CloudWriteFailure["diagnostic"] | null;
  debugEntitlement: DebugEntitlementState;
  authTransitioning: boolean;
}

export type SessionConnectResult =
  | { ok: true; conflict: boolean }
  | {
      ok: false;
      reason: "disabled" | "credentials" | "unauthorized" | "unavailable";
    };

export type SyncResult =
  | { ok: true }
  | {
      ok: false;
      reason: "guest" | "offline" | "conflict" | "permission" | "network";
    };

interface SyncOperation {
  readonly generation: number;
  readonly uid: string;
  requested: boolean;
  task: Promise<SyncResult>;
}

export type ProgressSessionListener = (snapshot: ProgressSnapshot) => void;

export interface OfficialMatchHandle {
  readonly sessionId: string;
  readonly ownerScope: ProgressOwnerScope;
  readonly mode: MatchPersistenceMode;
}

interface OfficialMatchState extends OfficialMatchHandle {
  officialBaseline: Progress;
  assisted: boolean;
  assistPersistenceConfirmed: boolean;
}

const GENERIC_LOGIN_MESSAGE = "无法登录或此账号未获 Snake 云存档授权。";
const LOGIN_SERVICE_UNAVAILABLE_MESSAGE =
  "账号验证服务暂不可用，已安全返回 Guest Progress；请稍后重试。";

const isPermissionFailure = (error: unknown): boolean => {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String(error.code).toLowerCase()
      : "";
  return (
    code.includes("permission-denied") || code.includes("permission_denied")
  );
};

export class ProgressSession {
  private readonly listeners = new Set<ProgressSessionListener>();
  private stateValue: ProgressSnapshot = {
    mode: "guest",
    progress: createDefaultProgress(),
    user: null,
    conflict: null,
    recoveredCorruptProgress: false,
    message: "Guest Progress 已保存到当前浏览器",
    cloudWriteDiagnostic: null,
    debugEntitlement: "denied",
    authTransitioning: false,
  };
  private currentStorageKey = GUEST_PROGRESS_KEY;
  private localEnvelope: LocalProgressEnvelope | null = null;
  private accountDirty = false;
  private resumeBlockedInMemory = false;
  private generation = 0;
  private localVersion = 0;
  private syncInFlight: SyncOperation | null = null;
  private authTail: Promise<void> = Promise.resolve();
  private authTransitionDepth = 0;
  private unsubscribeDebugEntitlement: (() => void) | null = null;
  private readonly officialMatches = new Map<string, OfficialMatchState>();

  constructor(
    private readonly localStore: LocalProgressStore,
    private readonly cloud: CloudGateway | null,
    private readonly now: () => number = Date.now,
    private readonly online: () => boolean = () =>
      typeof navigator === "undefined" || navigator.onLine !== false,
    private readonly matchPersistence: MatchPersistenceStore | null = null,
  ) {}

  private get state(): ProgressSnapshot {
    return this.stateValue;
  }

  private set state(value: ProgressSnapshot) {
    this.stateValue = value;
    if (this.listeners.size === 0) return;
    const snapshot = this.snapshot;
    for (const listener of this.listeners) {
      try {
        listener(snapshot);
      } catch {
        // A presentation listener must never interrupt persistence or sync.
      }
    }
  }

  get snapshot(): ProgressSnapshot {
    return {
      ...this.state,
      progress: cloneProgress(this.state.progress),
      user: this.state.user ? { ...this.state.user } : null,
      conflict: this.state.conflict
        ? {
            ...this.state.conflict,
            local: cloneProgress(this.state.conflict.local),
            cloud: cloneProgress(this.state.conflict.cloud),
          }
        : null,
    };
  }

  subscribe(listener: ProgressSessionListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose(): void {
    this.generation += 1;
    this.unsubscribeDebugEntitlement?.();
    this.unsubscribeDebugEntitlement = null;
    this.listeners.clear();
    this.officialMatches.clear();
  }

  beginOfficialMatch(
    sessionId: string,
    mode: MatchPersistenceMode,
  ): OfficialMatchHandle {
    if (this.authTransitionDepth > 0) {
      throw new Error("cannot begin Match during an identity transition");
    }
    if (!/^[A-Za-z0-9_-]{1,128}$/.test(sessionId)) {
      throw new Error("invalid Match sessionId");
    }
    const ownerScope = progressOwnerScope(this.state.user?.uid ?? null);
    const handle: OfficialMatchHandle = { sessionId, ownerScope, mode };
    const match: OfficialMatchState = {
      ...handle,
      officialBaseline: cloneProgress(this.state.progress),
      assisted: false,
      assistPersistenceConfirmed: false,
    };
    this.officialMatches.set(sessionId, match);
    if (mode === "endless" && this.matchPersistence) {
      const journal: EndlessSessionJournal = {
        version: 1,
        sessionId,
        ownerScope,
        baseRecords: { ...match.officialBaseline.records },
        checkpoint: {
          sessionMs: 0,
          aliveMs: 0,
          currentLength: 160,
          maxLength: 160,
          eliminations: 0,
          deaths: 0,
          foodCollected: 0,
        },
        assisted: false,
        updatedAt: this.now(),
      };
      if (!this.matchPersistence.writeJournal(journal)) {
        this.state = {
          ...this.state,
          message: "无尽会话日志不可用；本局不能启用 LEO ASSIST",
        };
      }
    }
    return handle;
  }

  checkpointEndlessMatch(
    handle: OfficialMatchHandle,
    checkpoint: EndlessCheckpoint,
  ): boolean {
    const match = this.matchFor(handle);
    if (!this.matchOwnerIsCurrent(match)) return false;
    return Boolean(
      match.mode === "endless" &&
      this.matchPersistence?.checkpoint(
        match.ownerScope,
        match.sessionId,
        checkpoint,
      ),
    );
  }

  markMatchAssisted(handle: OfficialMatchHandle): MarkAssistedResult {
    const match = this.matchFor(handle);
    if (!this.matchOwnerIsCurrent(match)) {
      return {
        ok: false,
        quarantineRequired: true,
        reason: "owner-mismatch",
      };
    }
    if (match.assistPersistenceConfirmed) return { ok: true };
    if (!this.matchPersistence) {
      return { ok: false, quarantineRequired: false, reason: "storage" };
    }
    const result = this.matchPersistence.markAssisted(
      match.ownerScope,
      match.sessionId,
      match.mode,
    );
    if (result.ok || result.quarantineRequired) match.assisted = true;
    match.assistPersistenceConfirmed = result.ok;
    return result;
  }

  isMatchAssisted(handle: OfficialMatchHandle): boolean {
    return this.matchFor(handle).assisted;
  }

  updateFromMatch(
    handle: OfficialMatchHandle,
    mutator: (progress: Progress) => void,
  ): ProgressSnapshot {
    const match = this.matchFor(handle);
    if (!this.matchOwnerIsCurrent(match)) return this.snapshot;
    return match.assisted ? this.snapshot : this.update(mutator);
  }

  officialBaselineFor(handle: OfficialMatchHandle): Progress {
    return cloneProgress(this.matchFor(handle).officialBaseline);
  }

  async syncAssistedSettings(
    handle: OfficialMatchHandle,
    settings: ProgressSettings,
  ): Promise<SyncResult> {
    const match = this.matchFor(handle);
    if (!match.assisted) {
      throw new Error("assisted settings sync requires an assisted Match");
    }
    let baseline = cloneProgress(match.officialBaseline);
    const user = this.state.user;
    const operationGeneration = this.generation;
    const operationUid = user?.uid ?? null;
    const operationScope = match.ownerScope;
    const operationStorageKey = this.currentStorageKey;
    const operationIsCurrent = (): boolean =>
      this.generation === operationGeneration &&
      (this.state.user?.uid ?? null) === operationUid &&
      progressOwnerScope(operationUid) === operationScope &&
      this.currentStorageKey === operationStorageKey &&
      this.officialMatches.get(match.sessionId) === match;
    if (!operationIsCurrent()) return { ok: false, reason: "permission" };
    if (user && this.cloud) {
      let remoteRead: CloudProgressRead | null;
      try {
        remoteRead = await this.readCloudProgress(user.uid);
      } catch {
        return { ok: false, reason: "network" };
      }
      if (!operationIsCurrent()) return { ok: false, reason: "permission" };
      if (remoteRead && remoteRead.progress.revision > baseline.revision) {
        const remote = remoteRead.progress;
        if (progressNeedsExplicitSource(baseline, remote)) {
          this.state = {
            ...this.state,
            mode: "conflict",
            conflict: {
              local: cloneProgress(baseline),
              cloud: cloneProgress(remote),
              needsExplicitSource: true,
            },
            message: "另一设备更新了设置或装备，请先解决 Progress Conflict",
          };
          return { ok: false, reason: "conflict" };
        }
        baseline = mergeProgress(baseline, remote, "local", this.now());
        baseline.revision = remote.revision;
      }
    }
    const candidate = cloneProgress(baseline);
    candidate.settings = { ...settings };
    candidate.updatedAt = this.now();
    const validation = validateProgress(candidate);
    if (!validation.ok) throw new Error(validation.reason);
    if (!operationIsCurrent()) return { ok: false, reason: "permission" };
    match.officialBaseline = cloneProgress(validation.value);
    this.state = {
      ...this.state,
      progress: cloneProgress(validation.value),
      message: user
        ? "Assisted Match 设置已安全隔离，等待同步"
        : "Assisted Match 设置已保存到 Guest Progress",
    };
    this.localVersion += 1;
    this.localEnvelope = this.writeLocal(
      this.currentStorageKey,
      validation.value,
      baseline.revision,
      Boolean(user),
      this.localEnvelope?.deviceId,
    );
    if (!user) return { ok: true };
    this.accountDirty = true;
    return this.sync();
  }

  recoverCurrentOwnerEndlessJournal(): ProgressSnapshot {
    if (!this.matchPersistence) return this.snapshot;
    const scope = progressOwnerScope(this.state.user?.uid ?? null);
    const journal = this.matchPersistence.readJournal(scope);
    const tombstone = this.matchPersistence.readTombstone(scope);
    if (!journal) {
      const activeMatch = tombstone
        ? this.officialMatches.get(tombstone.sessionId)
        : null;
      if (tombstone && (!activeMatch || activeMatch.ownerScope !== scope)) {
        this.matchPersistence.clearTombstone(scope, tombstone.sessionId);
      }
      return this.snapshot;
    }
    if (journal.assisted || tombstone?.sessionId === journal.sessionId) {
      const journalCleared = this.matchPersistence.clearJournal(
        scope,
        journal.sessionId,
      );
      if (journalCleared && tombstone?.sessionId === journal.sessionId) {
        this.matchPersistence.clearTombstone(scope, journal.sessionId);
      }
      return this.snapshot;
    }
    const recovered = recoverEndlessRecords(journal);
    const next = this.update((progress) => {
      for (const key of Object.keys(recovered) as Array<
        keyof Progress["records"]
      >) {
        progress.records[key] = Math.max(progress.records[key], recovered[key]);
      }
    });
    if (next.mode !== "storage-unavailable") {
      this.matchPersistence.clearJournal(scope, journal.sessionId);
    }
    return next;
  }

  finishOfficialMatch(
    handle: OfficialMatchHandle,
    options: { readonly progressPersisted?: boolean } = {},
  ): void {
    const match = this.matchFor(handle);
    if (!this.matchOwnerIsCurrent(match)) {
      if (this.matchPersistence && match.assisted && match.mode === "signal") {
        this.matchPersistence.clearTombstone(match.ownerScope, match.sessionId);
      }
      this.officialMatches.delete(match.sessionId);
      return;
    }
    if (this.matchPersistence) {
      let journalCleared = true;
      if (match.mode === "endless") {
        const journal = this.matchPersistence.readJournal(match.ownerScope);
        journalCleared =
          !journal ||
          (journal.sessionId === match.sessionId &&
            (match.assisted || options.progressPersisted !== false) &&
            this.matchPersistence.clearJournal(
              match.ownerScope,
              match.sessionId,
            ));
      }
      if (match.assisted && journalCleared) {
        this.matchPersistence.clearTombstone(match.ownerScope, match.sessionId);
      }
    }
    this.officialMatches.delete(match.sessionId);
  }

  private matchFor(handle: OfficialMatchHandle): OfficialMatchState {
    const match = this.officialMatches.get(handle.sessionId);
    if (
      !match ||
      match.ownerScope !== handle.ownerScope ||
      match.mode !== handle.mode
    ) {
      throw new Error("unknown or mismatched Match context");
    }
    return match;
  }

  private matchOwnerIsCurrent(match: OfficialMatchState): boolean {
    return (
      match.ownerScope === progressOwnerScope(this.state.user?.uid ?? null)
    );
  }

  start(): ProgressSnapshot {
    this.generation += 1;
    this.localVersion += 1;
    this.loadGuest("guest");
    return this.snapshot;
  }

  async login(email: string, password: string): Promise<SessionConnectResult> {
    if (!this.cloud) return { ok: false, reason: "disabled" };
    this.beginAuthTransition();
    try {
      this.loadGuest("guest");
      const attempt = ++this.generation;
      try {
        const user = await this.runAuthOperation(() =>
          this.cloud!.signIn(email.trim(), password),
        );
        if (attempt !== this.generation)
          return { ok: false, reason: "unavailable" };
        const result = await this.connectUser(user, attempt);
        if (result.ok) {
          this.localStore.clearAutoResumeBlock();
          this.resumeBlockedInMemory = false;
          this.recoverCurrentOwnerEndlessJournal();
        }
        return result;
      } catch {
        if (attempt === this.generation) {
          await this.signOutCloud();
          this.loadGuest("guest");
          this.state = { ...this.state, message: GENERIC_LOGIN_MESSAGE };
        }
        return { ok: false, reason: "credentials" };
      }
    } finally {
      this.endAuthTransition();
    }
  }

  async resume(): Promise<SessionConnectResult> {
    if (!this.cloud) return { ok: false, reason: "disabled" };
    this.beginAuthTransition();
    try {
      const attempt = ++this.generation;
      const resumeBlock = this.localStore.readAutoResumeBlock();
      if (
        this.resumeBlockedInMemory ||
        resumeBlock.blocked ||
        !resumeBlock.persistenceAvailable
      ) {
        this.resumeBlockedInMemory = true;
        const signedOut = await this.signOutCloud();
        if (!signedOut) {
          if (attempt === this.generation) this.markLogoutIncomplete();
          return { ok: false, reason: "unavailable" };
        }
        if (attempt === this.generation) this.loadGuest("guest");
        const cleared = this.localStore.clearAutoResumeBlock();
        this.resumeBlockedInMemory = !cleared;
        return { ok: false, reason: "credentials" };
      }
      this.loadGuest("guest");
      try {
        const user = await this.runAuthOperation(() =>
          this.cloud!.currentUser(),
        );
        if (!user || attempt !== this.generation)
          return { ok: false, reason: "credentials" };
        const result = await this.connectUser(user, attempt);
        if (result.ok) this.recoverCurrentOwnerEndlessJournal();
        return result;
      } catch {
        return { ok: false, reason: "unavailable" };
      }
    } finally {
      this.endAuthTransition();
    }
  }

  private async connectUser(
    user: CloudUser,
    attempt: number,
  ): Promise<SessionConnectResult> {
    if (!this.cloud) return { ok: false, reason: "disabled" };
    try {
      const allowed = await this.cloud.isAllowed(user.uid);
      if (attempt !== this.generation)
        return { ok: false, reason: "unavailable" };
      if (!allowed) {
        await this.signOutCloud();
        if (attempt === this.generation) {
          this.loadGuest("auth-unauthorized");
          this.state = { ...this.state, message: GENERIC_LOGIN_MESSAGE };
        }
        return { ok: false, reason: "unauthorized" };
      }

      const uidKey = uidProgressKey(user.uid);
      let uidLocal = this.localStore.read(uidKey);
      const guest = this.localStore.read(GUEST_PROGRESS_KEY);
      const defaultGuest = createDefaultProgress(
        guest.envelope?.progress.updatedAt ?? 0,
      );
      const guestHasProgress = Boolean(
        guest.envelope &&
        !progressContentEqual(guest.envelope.progress, defaultGuest),
      );
      let localCandidate =
        uidLocal.envelope?.progress ??
        guest.envelope?.progress ??
        createDefaultProgress(this.now());
      let hasAccountLocal = Boolean(uidLocal.envelope);
      let localChangedDuringRead = false;
      let baseRevision =
        uidLocal.envelope?.baseCloudRevision ?? localCandidate.revision;
      this.currentStorageKey = uidKey;
      this.localEnvelope = uidLocal.envelope;
      this.accountDirty = Boolean(uidLocal.envelope?.dirty);
      this.state = {
        ...this.state,
        mode: "syncing",
        progress: cloneProgress(localCandidate),
        user,
        conflict: null,
        recoveredCorruptProgress:
          uidLocal.recoveredFromCorruption || guest.recoveredFromCorruption,
        message: "正在读取 Account Progress…",
      };
      this.startDebugEntitlement(user);

      let cloudRead: CloudProgressRead | null;
      const readLocalVersion = this.localVersion;
      try {
        cloudRead = await this.readCloudProgress(user.uid);
      } catch (error) {
        if (attempt !== this.generation)
          return { ok: false, reason: "unavailable" };
        if (isPermissionFailure(error)) {
          this.generation += 1;
          this.loadGuest("guest");
          this.state = {
            ...this.state,
            message: "云存档权限已撤销，已返回 Guest Progress",
          };
          void this.signOutCloud();
          return { ok: false, reason: "unauthorized" };
        }
        this.state = {
          ...this.state,
          mode: "offline",
          progress: cloneProgress(localCandidate),
          message: "云端暂不可用，正在使用本地 Account Progress",
        };
        this.accountDirty = true;
        this.localEnvelope = this.writeLocal(
          uidKey,
          localCandidate,
          baseRevision,
          true,
          uidLocal.envelope?.deviceId,
        );
        return { ok: true, conflict: false };
      }
      if (attempt !== this.generation)
        return { ok: false, reason: "unavailable" };
      if (readLocalVersion !== this.localVersion) {
        localChangedDuringRead = true;
        const refreshed = this.localStore.read(uidKey);
        uidLocal = refreshed;
        localCandidate = cloneProgress(this.state.progress);
        hasAccountLocal = hasAccountLocal || Boolean(refreshed.envelope);
        baseRevision = refreshed.envelope?.baseCloudRevision ?? baseRevision;
      }
      if (cloudRead) {
        const validation = validateProgress(cloudRead.progress);
        const minimumRevision = cloudRead.sourceSchemaVersion === 0 ? 0 : 1;
        if (!validation.ok || validation.value.revision < minimumRevision) {
          this.state = {
            ...this.state,
            mode: "offline",
            progress: cloneProgress(localCandidate),
            message: "云端进度无效，已保留本地 Account Progress",
          };
          this.accountDirty = true;
          this.localEnvelope = this.writeLocal(
            uidKey,
            localCandidate,
            baseRevision,
            true,
            uidLocal.envelope?.deviceId,
          );
          return { ok: true, conflict: false };
        }
        const cloudValue = validation.value;
        const differs = !progressContentEqual(localCandidate, cloudValue);
        const localDirty =
          localChangedDuringRead || Boolean(uidLocal.envelope?.dirty);
        const bothChanged = hasAccountLocal
          ? localDirty && cloudValue.revision !== baseRevision
          : guestHasProgress;
        if (differs && bothChanged) {
          this.state = {
            ...this.state,
            mode: "conflict",
            progress: cloneProgress(localCandidate),
            conflict: {
              local: cloneProgress(localCandidate),
              cloud: cloneProgress(cloudValue),
              needsExplicitSource: progressNeedsExplicitSource(
                localCandidate,
                cloudValue,
              ),
            },
            message: "Guest/Account Progress 都有修改，请选择设置和装备来源",
          };
          this.accountDirty = true;
          this.localEnvelope = this.writeLocal(
            uidKey,
            localCandidate,
            baseRevision,
            true,
            uidLocal.envelope?.deviceId,
          );
          return { ok: true, conflict: true };
        }
        if (differs && localDirty && cloudValue.revision === baseRevision) {
          this.state = {
            ...this.state,
            mode: this.online() ? "account" : "offline",
            progress: cloneProgress(localCandidate),
            message: "本地 Account Progress 等待同步",
          };
          this.accountDirty = true;
          this.localEnvelope = this.writeLocal(
            uidKey,
            localCandidate,
            cloudValue.revision,
            true,
            uidLocal.envelope?.deviceId,
          );
          if (this.online()) await this.sync();
          return { ok: true, conflict: false };
        }
        if (cloudRead.needsUpgrade) {
          this.state = {
            ...this.state,
            mode: this.online() ? "account" : "offline",
            progress: cloneProgress(cloudValue),
            message: "Account Progress 已迁移到 V3，等待安全写回",
          };
          this.accountDirty = true;
          this.localEnvelope = this.writeLocal(
            uidKey,
            cloudValue,
            cloudValue.revision,
            true,
            uidLocal.envelope?.deviceId,
          );
          if (this.online()) await this.sync();
          return { ok: true, conflict: false };
        }
        this.state = {
          ...this.state,
          mode: "account",
          progress: cloneProgress(cloudValue),
          message: "Account Progress 已连接",
        };
        this.accountDirty = false;
        this.localEnvelope = this.writeLocal(
          uidKey,
          cloudValue,
          cloudValue.revision,
          false,
          uidLocal.envelope?.deviceId,
        );
        return { ok: true, conflict: false };
      }

      const firstAccountProgress = cloneProgress(localCandidate);
      firstAccountProgress.revision = 0;
      firstAccountProgress.updatedAt = this.now();
      this.state = {
        ...this.state,
        mode: this.online() ? "account" : "offline",
        progress: firstAccountProgress,
        message: "Account Progress 已连接，等待首次同步",
      };
      this.accountDirty = true;
      this.localEnvelope = this.writeLocal(
        uidKey,
        firstAccountProgress,
        0,
        true,
        uidLocal.envelope?.deviceId,
      );
      if (this.online()) await this.sync();
      return { ok: true, conflict: false };
    } catch {
      if (attempt === this.generation) {
        void this.signOutCloud();
        this.loadGuest("guest");
        this.state = {
          ...this.state,
          message: LOGIN_SERVICE_UNAVAILABLE_MESSAGE,
        };
      }
      return { ok: false, reason: "unavailable" };
    }
  }

  update(mutator: (progress: Progress) => void): ProgressSnapshot {
    const next = cloneProgress(this.state.progress);
    mutator(next);
    next.updatedAt = this.now();
    const validation = validateProgress(next);
    if (!validation.ok) throw new Error(validation.reason);
    if (!progressIsMonotonicFrom(this.state.progress, validation.value)) {
      throw new Error(
        "records, currency, tutorial and owned cosmetics cannot move backwards",
      );
    }
    const accountOffline = Boolean(
      this.state.user && !this.state.conflict && !this.online(),
    );
    this.state = {
      ...this.state,
      mode: accountOffline ? "offline" : this.state.mode,
      progress: cloneProgress(validation.value),
      message: accountOffline
        ? "离线：Account Progress 已保存在本地"
        : this.state.user
          ? "Account Progress 已保存到本地，等待同步"
          : "Guest Progress 已保存到当前浏览器",
    };
    this.localVersion += 1;
    this.persist(Boolean(this.state.user));
    if (
      this.state.user &&
      this.state.mode !== "syncing" &&
      !this.state.conflict &&
      this.online()
    ) {
      void this.sync();
    }
    return this.snapshot;
  }

  deferConflict(): ProgressSnapshot {
    if (!this.state.conflict) return this.snapshot;
    this.state = {
      ...this.state,
      mode: "offline",
      message: "冲突尚未解决；继续本地游戏，不会覆盖云端",
    };
    return this.snapshot;
  }

  async resolveConflict(source: ExplicitProgressSource): Promise<SyncResult> {
    const conflict = this.state.conflict;
    if (!conflict || !this.state.user) return { ok: false, reason: "conflict" };
    const currentLocal = cloneProgress(this.state.progress);
    if (source === "local")
      this.localStore.backupCandidate("cloud", conflict.cloud);
    else this.localStore.backupCandidate("local", currentLocal);
    const merged = mergeProgress(
      currentLocal,
      conflict.cloud,
      source,
      this.now(),
    );
    merged.revision = conflict.cloud.revision;
    this.state = {
      ...this.state,
      mode: "syncing",
      progress: merged,
      conflict: null,
      message: "正在同步已解决的 Progress Conflict…",
    };
    this.localVersion += 1;
    this.accountDirty = true;
    this.localEnvelope = this.writeLocal(
      this.currentStorageKey,
      merged,
      conflict.cloud.revision,
      true,
      this.localEnvelope?.deviceId,
    );
    return this.sync();
  }

  async sync(): Promise<SyncResult> {
    const user = this.state.user;
    if (!this.cloud || !user) return { ok: false, reason: "guest" };
    const generation = this.generation;
    if (
      this.syncInFlight?.generation === generation &&
      this.syncInFlight.uid === user.uid
    ) {
      return this.syncInFlight.task;
    }
    const operation: SyncOperation = {
      generation,
      uid: user.uid,
      requested: false,
      task: Promise.resolve({ ok: false, reason: "guest" }),
    };
    operation.task = (async () => {
      let result: SyncResult;
      do {
        operation.requested = false;
        result = await this.syncOnce(operation);
      } while (
        result.ok &&
        operation.requested &&
        operation.generation === this.generation &&
        this.state.user?.uid === operation.uid
      );
      return result;
    })();
    this.syncInFlight = operation;
    try {
      return await operation.task;
    } finally {
      if (this.syncInFlight === operation) this.syncInFlight = null;
    }
  }

  async handleOnline(): Promise<void> {
    if (!this.online() || !this.state.user || this.state.conflict) {
      return;
    }
    if (!this.accountDirty) {
      if (this.state.mode === "offline") {
        this.state = {
          ...this.state,
          mode: "account",
          message: "Account Progress 已连接",
        };
      }
      return;
    }
    await this.sync();
  }

  handleOffline(): void {
    if (
      !this.state.user ||
      this.state.conflict ||
      this.state.mode === "storage-unavailable"
    ) {
      return;
    }
    this.state = {
      ...this.state,
      mode: "offline",
      message: "离线：Account Progress 已保存在本地",
    };
  }

  private async syncOnce(operation: SyncOperation): Promise<SyncResult> {
    if (
      !this.cloud ||
      !this.state.user ||
      operation.generation !== this.generation ||
      operation.uid !== this.state.user.uid
    ) {
      return { ok: false, reason: "guest" };
    }
    if (this.state.conflict) return { ok: false, reason: "conflict" };
    if (!this.online()) {
      this.state = {
        ...this.state,
        mode: "offline",
        message: "离线：Account Progress 已保存在本地",
      };
      return { ok: false, reason: "offline" };
    }
    const operationLocalVersion = this.localVersion;
    const user = this.state.user;
    const candidate = cloneProgress(this.state.progress);
    const expectedRevision =
      this.localEnvelope?.baseCloudRevision ?? candidate.revision;
    this.state = {
      ...this.state,
      mode: "syncing",
      message: "正在同步 Account Progress…",
      cloudWriteDiagnostic: null,
    };
    const result = await this.cloud.writeProgress(
      user.uid,
      candidate,
      expectedRevision,
    );
    if (
      operation.generation !== this.generation ||
      this.state.user?.uid !== operation.uid
    )
      return { ok: false, reason: "guest" };
    if (result.ok)
      return this.handleSuccessfulWrite(
        result,
        operationLocalVersion,
        operation,
      );
    if (result.reason === "permission") {
      this.generation += 1;
      this.loadGuest("guest");
      this.state = {
        ...this.state,
        message: "云存档权限已撤销，已返回 Guest Progress",
        cloudWriteDiagnostic: result.diagnostic,
      };
      void this.signOutCloud();
      return { ok: false, reason: "permission" };
    }
    if (result.reason === "conflict") {
      try {
        const cloud = await this.readCloudProgress(user.uid);
        const validation = validateProgress(cloud?.progress);
        if (
          validation.ok &&
          operation.generation === this.generation &&
          this.state.user?.uid === operation.uid
        ) {
          this.state = {
            ...this.state,
            mode: "conflict",
            conflict: {
              local: cloneProgress(this.state.progress),
              cloud: cloneProgress(validation.value),
              needsExplicitSource: progressNeedsExplicitSource(
                this.state.progress,
                validation.value,
              ),
            },
            message: "另一设备更新了 Account Progress，请选择来源",
            cloudWriteDiagnostic: result.diagnostic,
          };
          this.persist(true);
          return { ok: false, reason: "conflict" };
        }
      } catch {
        // Keep the local candidate below when conflict details cannot be read.
      }
    }
    this.state = {
      ...this.state,
      mode: "offline",
      message:
        result.reason === "conflict"
          ? "云端版本冲突，已保留本地 Account Progress"
          : "云端暂不可用，Account Progress 已保存在本地",
      cloudWriteDiagnostic: result.diagnostic,
    };
    this.persist(true);
    return {
      ok: false,
      reason: result.reason === "conflict" ? "conflict" : "network",
    };
  }

  private handleSuccessfulWrite(
    result: Extract<CloudWriteResult, { ok: true }>,
    operationLocalVersion: number,
    operation: SyncOperation,
  ): SyncResult {
    if (operationLocalVersion !== this.localVersion) {
      const pending = cloneProgress(this.state.progress);
      pending.revision = result.progress.revision;
      this.state = {
        ...this.state,
        mode: "syncing",
        progress: pending,
        message: "本地有更新，正在继续同步…",
      };
      this.accountDirty = true;
      this.localEnvelope = this.writeLocal(
        this.currentStorageKey,
        pending,
        result.progress.revision,
        true,
        this.localEnvelope?.deviceId,
      );
      operation.requested = true;
      return { ok: true };
    }
    this.state = {
      ...this.state,
      mode: "account",
      progress: cloneProgress(result.progress),
      conflict: null,
      message: "Account Progress 已同步",
      cloudWriteDiagnostic: null,
    };
    this.accountDirty = false;
    this.localEnvelope = this.writeLocal(
      this.currentStorageKey,
      result.progress,
      result.progress.revision,
      false,
      this.localEnvelope?.deviceId,
    );
    return { ok: true };
  }

  async logout(): Promise<boolean> {
    this.beginAuthTransition();
    try {
      this.localStore.blockAutoResume();
      this.resumeBlockedInMemory = true;
      const attempt = ++this.generation;
      if (this.state.user) {
        this.state = {
          ...this.state,
          message: "正在退出 Account Progress…",
        };
      }
      const signedOut = await this.signOutCloud();
      if (signedOut) {
        if (attempt === this.generation) this.loadGuest("guest");
        const cleared = this.localStore.clearAutoResumeBlock();
        this.resumeBlockedInMemory = !cleared;
        return true;
      }
      if (attempt === this.generation) this.markLogoutIncomplete();
      return false;
    } finally {
      this.endAuthTransition();
    }
  }

  private markLogoutIncomplete(): void {
    if (!this.state.user) return;
    this.state = {
      ...this.state,
      mode:
        this.state.mode === "storage-unavailable"
          ? "storage-unavailable"
          : this.state.conflict
            ? "conflict"
            : "offline",
      message: "退出未完成，账户仍连接，请重试",
    };
  }

  private async signOutCloud(): Promise<boolean> {
    if (!this.cloud) return true;
    try {
      await this.runAuthOperation(() => this.cloud!.signOut());
      return true;
    } catch {
      return false;
    }
  }

  private async readCloudProgress(
    uid: string,
  ): Promise<CloudProgressRead | null> {
    if (!this.cloud) return null;
    if (this.cloud.readProgressWithMetadata) {
      return this.cloud.readProgressWithMetadata(uid);
    }
    const progress = await this.cloud.readProgress(uid);
    return progress
      ? {
          progress,
          sourceSchemaVersion: 3,
          needsUpgrade: false,
        }
      : null;
  }

  private startDebugEntitlement(user: CloudUser): void {
    this.unsubscribeDebugEntitlement?.();
    this.unsubscribeDebugEntitlement = null;
    if (!this.cloud?.watchDebugEntitlement) {
      this.state = { ...this.state, debugEntitlement: "denied" };
      return;
    }
    this.state = { ...this.state, debugEntitlement: "pending" };
    this.unsubscribeDebugEntitlement = this.cloud.watchDebugEntitlement(
      user.uid,
      (debugEntitlement) => {
        if (this.state.user?.uid !== user.uid) return;
        this.state = { ...this.state, debugEntitlement };
      },
    );
  }

  private runAuthOperation<T>(operation: () => Promise<T>): Promise<T> {
    const task = this.authTail.then(operation, operation);
    this.authTail = task.then(
      () => undefined,
      () => undefined,
    );
    return task;
  }

  private beginAuthTransition(): void {
    this.authTransitionDepth += 1;
    if (this.authTransitionDepth === 1) {
      this.state = { ...this.state, authTransitioning: true };
    }
  }

  private endAuthTransition(): void {
    this.authTransitionDepth = Math.max(0, this.authTransitionDepth - 1);
    if (this.authTransitionDepth === 0 && this.state.authTransitioning) {
      this.state = { ...this.state, authTransitioning: false };
    }
  }

  private loadGuest(mode: "guest" | "auth-unauthorized"): void {
    this.unsubscribeDebugEntitlement?.();
    this.unsubscribeDebugEntitlement = null;
    this.currentStorageKey = GUEST_PROGRESS_KEY;
    this.accountDirty = false;
    const local = this.localStore.read(GUEST_PROGRESS_KEY);
    const progress =
      local.envelope?.progress ?? createDefaultProgress(this.now());
    this.localEnvelope = local.envelope;
    this.state = {
      mode: local.persistenceAvailable ? mode : "storage-unavailable",
      progress: cloneProgress(progress),
      user: null,
      conflict: null,
      recoveredCorruptProgress: local.recoveredFromCorruption,
      message: local.persistenceAvailable
        ? "Guest Progress 已保存到当前浏览器"
        : "浏览器存储不可用；本局仅保存在内存",
      cloudWriteDiagnostic: null,
      debugEntitlement: "denied",
      authTransitioning: this.authTransitionDepth > 0,
    };
    if (!local.envelope) this.persist(false);
  }

  private writeLocal(
    key: string,
    progress: Progress,
    baseCloudRevision: number,
    dirty: boolean,
    existingDeviceId?: string,
  ): LocalProgressEnvelope | null {
    try {
      return this.localStore.write(
        key,
        progress,
        baseCloudRevision,
        dirty,
        existingDeviceId,
      );
    } catch {
      this.state = {
        ...this.state,
        mode: "storage-unavailable",
        message: "浏览器存储不可用；本局仅保存在内存",
      };
      return null;
    }
  }

  private persist(dirty: boolean): void {
    this.accountDirty = Boolean(this.state.user) && dirty;
    if (this.state.conflict) {
      this.state = {
        ...this.state,
        conflict: {
          ...this.state.conflict,
          local: cloneProgress(this.state.progress),
        },
      };
    }
    const envelope = this.writeLocal(
      this.currentStorageKey,
      this.state.progress,
      this.localEnvelope?.baseCloudRevision ?? this.state.progress.revision,
      dirty,
      this.localEnvelope?.deviceId,
    );
    this.localEnvelope = envelope;
    if (!envelope) return;
    if (this.state.mode === "storage-unavailable") {
      this.state = {
        ...this.state,
        mode: this.state.user ? "offline" : "guest",
      };
    }
  }
}
